import React, { useEffect, useMemo, useState } from 'react';
import { api, authHeaders } from './api/client';
import { AuthProvider, useAuth } from './auth/AuthContext';
import TimrLogo from './components/TimrLogo';
import { SuspiciousPanel, AttendancePolicyPanel, GeofenceSitesPanel, KioskPanel, QrChallengePanel } from './components/AttendanceFeatures';

function toCsv(rows, headers) {
  const escape = (value) => {
    const text = value === null || value === undefined ? '' : String(value);
    const normalized = text.replace(/\r?\n|\r/g, ' ');
    if (/[",]/.test(normalized)) {
      return `"${normalized.replace(/"/g, '""')}"`;
    }
    return normalized;
  };

  const lines = [headers.map((h) => escape(h.label)).join(',')];
  for (const row of rows) {
    lines.push(headers.map((h) => escape(row[h.key])).join(','));
  }
  return `${lines.join('\n')}\n`;
}

function downloadCsv(fileName, csvText) {
  const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

const EXPORT_TEMPLATE_SOURCES = [
  { key: 'work_date', label: 'Work Date' },
  { key: 'employee_code', label: 'Employee Code' },
  { key: 'employee_name', label: 'Employee Name' },
  { key: 'department', label: 'Department' },
  { key: 'clock_in', label: 'Clock In (UTC)' },
  { key: 'clock_out', label: 'Clock Out (UTC)' },
  { key: 'regular_hours', label: 'Regular Hours' },
  { key: 'overtime_hours', label: 'Overtime Hours' },
  { key: 'total_hours', label: 'Total Hours' },
  { key: 'status', label: 'Status' },
  { key: 'attendance_source', label: 'Attendance Source' },
  { key: 'approved_by_user_id', label: 'Approved By User ID' },
];

const DEFAULT_EXPORT_TEMPLATE = {
  id: 'default',
  name: 'Default TIMR Payroll CSV',
  columns: [
    { label: 'Work Date', key: 'work_date' },
    { label: 'Employee Code', key: 'employee_code' },
    { label: 'Employee Name', key: 'employee_name' },
    { label: 'Department', key: 'department' },
    { label: 'Clock In (UTC)', key: 'clock_in' },
    { label: 'Clock Out (UTC)', key: 'clock_out' },
    { label: 'Regular Hours', key: 'regular_hours' },
    { label: 'Overtime Hours', key: 'overtime_hours' },
    { label: 'Total Hours', key: 'total_hours' },
    { label: 'Status', key: 'status' },
    { label: 'Attendance Source', key: 'attendance_source' },
  ],
};

function SignInCard() {
  const { signIn, isAuthLoading, authError } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  async function handleSubmit(event) {
    event.preventDefault();
    await signIn({ username, password });
  }

  return (
    <section className="card">
      <h2>Sign In</h2>
      <p className="subtle">Sign in with your company credentials.</p>
      <form onSubmit={(event) => void handleSubmit(event)} className="row">
        <label>
          Username
          <input value={username} onChange={(e) => setUsername(e.target.value)} required />
        </label>
        <label>
          Password
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        </label>
        <div style={{ alignSelf: 'end' }}>
          <button type="submit" disabled={isAuthLoading}>{isAuthLoading ? 'Signing in...' : 'Sign in'}</button>
        </div>
      </form>
      {authError ? <p style={{ color: '#b91c1c', marginTop: 10 }}>{authError}</p> : null}
    </section>
  );
}

function TimesheetWorkspace() {
  const { token, currentUser, signOut } = useAuth();
  const [selfAttendance, setSelfAttendance] = useState([]);
  const [pendingTeam, setPendingTeam] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [users, setUsers] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [employeeForm, setEmployeeForm] = useState({
    full_name: '',
    employee_code: '',
    basic_salary: '',
    department: '',
    position: '',
    tax_id: '',
  });
  const [userForm, setUserForm] = useState({
    username: '',
    full_name: '',
    password: '',
    role: 'employee',
    employee_id: '',
  });
  const [apiKeyForm, setApiKeyForm] = useState({ name: '' });
  const [webhookForm, setWebhookForm] = useState({
    name: '',
    target_url: '',
    event_filter: 'timr.*',
    signing_secret: '',
    is_active: true,
  });
  const [generatedApiKey, setGeneratedApiKey] = useState('');
  const [employeeImportFile, setEmployeeImportFile] = useState(null);
  const [isEmployeeSubmitting, setIsEmployeeSubmitting] = useState(false);
  const [isEmployeeImporting, setIsEmployeeImporting] = useState(false);
  const [isUserSubmitting, setIsUserSubmitting] = useState(false);
  const [isApiKeySubmitting, setIsApiKeySubmitting] = useState(false);
  const [isWebhookSubmitting, setIsWebhookSubmitting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportMonth, setExportMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [activeSection, setActiveSection] = useState('timesheets');
  const [selectedTemplateId, setSelectedTemplateId] = useState('default');
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [templateName, setTemplateName] = useState('');
  const [templateColumns, setTemplateColumns] = useState(DEFAULT_EXPORT_TEMPLATE.columns);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [onLunch, setOnLunch] = useState(false);

  const normalizedRole = useMemo(() => {
    const role = (currentUser?.role || '').toLowerCase();
    if (role === 'system_admin' || role === 'company_admin') {
      return 'admin';
    }
    return role;
  }, [currentUser]);

  const canReview = useMemo(() => ['manager', 'admin'].includes(normalizedRole), [normalizedRole]);
  const canUseSelfAttendance = useMemo(() => ['employee', 'manager'].includes(normalizedRole), [normalizedRole]);
  const canManageEmployees = useMemo(() => ['admin', 'hr'].includes(normalizedRole), [normalizedRole]);
  const canManageUsers = useMemo(() => ['admin'].includes(normalizedRole), [normalizedRole]);
  const canManageApiKeys = useMemo(() => ['admin'].includes(normalizedRole), [normalizedRole]);
  const canManageWebhooks = useMemo(() => ['admin'].includes(normalizedRole), [normalizedRole]);
  const canExportApproved = useMemo(() => ['manager', 'admin', 'hr'].includes(normalizedRole), [normalizedRole]);

  const templateStorageKey = useMemo(
    () => `timr.export.templates.${currentUser?.company_id || 'default'}`,
    [currentUser],
  );

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(templateStorageKey);
      const parsed = raw ? JSON.parse(raw) : [];
      const templates = Array.isArray(parsed) ? parsed : [];
      setSavedTemplates(templates);
      if (templates.length > 0) {
        setSelectedTemplateId(templates[0].id);
        setTemplateName(templates[0].name || '');
        setTemplateColumns(Array.isArray(templates[0].columns) && templates[0].columns.length ? templates[0].columns : DEFAULT_EXPORT_TEMPLATE.columns);
      } else {
        setSelectedTemplateId('default');
        setTemplateName('');
        setTemplateColumns(DEFAULT_EXPORT_TEMPLATE.columns);
      }
    } catch {
      setSavedTemplates([]);
      setSelectedTemplateId('default');
      setTemplateName('');
      setTemplateColumns(DEFAULT_EXPORT_TEMPLATE.columns);
    }
  }, [templateStorageKey]);

  useEffect(() => {
    if (!token) return;

    let cancelled = false;
    async function load() {
      setIsLoading(true);
      setError('');
      try {
        const requests = [
          canUseSelfAttendance ? api.get('/api/self/attendance', authHeaders(token)) : Promise.resolve({ data: [] }),
          canReview ? api.get('/api/manager/attendance/timesheets', authHeaders(token, { status_filter: 'pending' })) : Promise.resolve({ data: [] }),
          canManageEmployees ? api.get('/api/employees', authHeaders(token)) : Promise.resolve({ data: [] }),
          canManageUsers ? api.get('/api/users', authHeaders(token)) : Promise.resolve({ data: [] }),
          canManageApiKeys ? api.get('/api/admin/api-keys', authHeaders(token)) : Promise.resolve({ data: [] }),
          canManageWebhooks ? api.get('/api/integrations/webhooks', authHeaders(token)) : Promise.resolve({ data: [] }),
        ];
        const [mineRes, teamRes, employeesRes, usersRes, apiKeysRes, webhooksRes] = await Promise.all(requests);
        if (!cancelled) {
          setSelfAttendance(Array.isArray(mineRes.data) ? mineRes.data : []);
          setPendingTeam(Array.isArray(teamRes.data) ? teamRes.data : []);
          setEmployees(Array.isArray(employeesRes.data) ? employeesRes.data : []);
          setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
          setApiKeys(Array.isArray(apiKeysRes.data) ? apiKeysRes.data : []);
          setWebhooks(Array.isArray(webhooksRes.data) ? webhooksRes.data : []);
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError?.response?.data?.detail || loadError?.message || 'Unable to load TIMR workspace data');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [token, canUseSelfAttendance, canReview, canManageEmployees, canManageUsers, canManageApiKeys, canManageWebhooks, refreshKey]);

  async function getLocation() {
    if (!navigator.geolocation) return {};
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy_m: pos.coords.accuracy }),
        () => resolve({}),
        { enableHighAccuracy: true, timeout: 10000 },
      );
    });
  }

  async function captureFace() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: 480, height: 480 } });
      const video = document.createElement('video');
      video.srcObject = stream;
      video.setAttribute('playsinline', '');
      await video.play();
      await new Promise((r) => setTimeout(r, 800));
      const canvas = document.createElement('canvas');
      canvas.width = 480;
      canvas.height = 480;
      canvas.getContext('2d').drawImage(video, 0, 0, 480, 480);
      stream.getTracks().forEach((t) => t.stop());
      const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
      const base64 = dataUrl.split(',')[1];
      const verifyRes = await api.post('/api/self/attendance/face-verify', { image_base64: base64 }, authHeaders(token));
      return { face_verified: verifyRes.data?.face_verified || false, face_match_score: verifyRes.data?.face_match_score || 0, liveness_score: verifyRes.data?.liveness_score || 0 };
    } catch {
      return { face_verified: false };
    }
  }

  async function quickClock(action) {
    setIsLoading(true);
    setError('');
    try {
      const geo = await getLocation();
      const face = await captureFace();
      const capture = { method: 'manual', ...geo, ...face };
      await api.post(`/api/self/attendance/${action}`, { capture }, authHeaders(token));
      setRefreshKey((v) => v + 1);
    } catch (submitError) {
      setError(submitError?.response?.data?.detail || submitError?.message || `Unable to ${action}`);
    } finally {
      setIsLoading(false);
    }
  }

  async function review(timesheetId, decision) {
    setIsLoading(true);
    setError('');
    try {
      await api.post(`/api/manager/attendance/timesheets/${timesheetId}/${decision}`, {}, authHeaders(token));
      setRefreshKey((v) => v + 1);
    } catch (submitError) {
      setError(submitError?.response?.data?.detail || submitError?.message || `Unable to ${decision} timesheet`);
    } finally {
      setIsLoading(false);
    }
  }

  async function createEmployee(event) {
    event.preventDefault();
    setIsEmployeeSubmitting(true);
    setError('');
    try {
      await api.post(
        '/api/employees',
        {
          full_name: employeeForm.full_name,
          employee_code: employeeForm.employee_code,
          basic_salary: Number(employeeForm.basic_salary),
          department: employeeForm.department || null,
          position: employeeForm.position || null,
          tax_id: employeeForm.tax_id || null,
          taxable_allowance: 0,
          non_taxable_allowance: 0,
          other_deductions: 0,
        },
        authHeaders(token),
      );
      setEmployeeForm({
        full_name: '',
        employee_code: '',
        basic_salary: '',
        department: '',
        position: '',
        tax_id: '',
      });
      setRefreshKey((v) => v + 1);
    } catch (submitError) {
      setError(submitError?.response?.data?.detail || submitError?.message || 'Unable to create employee');
    } finally {
      setIsEmployeeSubmitting(false);
    }
  }

  async function bulkImportEmployees() {
    if (!employeeImportFile) return;
    setIsEmployeeImporting(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', employeeImportFile);
      await api.post('/api/employees/bulk-import', formData, authHeaders(token));
      setEmployeeImportFile(null);
      setRefreshKey((v) => v + 1);
    } catch (submitError) {
      setError(submitError?.response?.data?.detail || submitError?.message || 'Unable to import employees');
    } finally {
      setIsEmployeeImporting(false);
    }
  }

  async function createUser(event) {
    event.preventDefault();
    setIsUserSubmitting(true);
    setError('');
    try {
      const selectedEmployeeId = userForm.employee_id ? Number(userForm.employee_id) : null;
      await api.post(
        '/api/users',
        {
          username: userForm.username,
          full_name: userForm.full_name,
          password: userForm.password,
          role: userForm.role,
          employee_id: userForm.role === 'employee' ? selectedEmployeeId : null,
          company_ids: [],
        },
        authHeaders(token),
      );
      setUserForm({
        username: '',
        full_name: '',
        password: '',
        role: 'employee',
        employee_id: '',
      });
      setRefreshKey((v) => v + 1);
    } catch (submitError) {
      setError(submitError?.response?.data?.detail || submitError?.message || 'Unable to create user');
    } finally {
      setIsUserSubmitting(false);
    }
  }

  async function createApiKey(event) {
    event.preventDefault();
    setIsApiKeySubmitting(true);
    setError('');
    setGeneratedApiKey('');
    try {
      const response = await api.post('/api/admin/api-keys', { name: apiKeyForm.name }, authHeaders(token));
      setGeneratedApiKey(response.data?.key || '');
      setApiKeyForm({ name: '' });
      setRefreshKey((v) => v + 1);
    } catch (submitError) {
      setError(submitError?.response?.data?.detail || submitError?.message || 'Unable to create API key');
    } finally {
      setIsApiKeySubmitting(false);
    }
  }

  async function createWebhook(event) {
    event.preventDefault();
    setIsWebhookSubmitting(true);
    setError('');
    try {
      await api.post(
        '/api/integrations/webhooks',
        {
          name: webhookForm.name,
          target_url: webhookForm.target_url,
          event_filter: webhookForm.event_filter || '*',
          signing_secret: webhookForm.signing_secret || null,
          is_active: Boolean(webhookForm.is_active),
        },
        authHeaders(token),
      );
      setWebhookForm({
        name: '',
        target_url: '',
        event_filter: 'timr.*',
        signing_secret: '',
        is_active: true,
      });
      setRefreshKey((v) => v + 1);
    } catch (submitError) {
      setError(submitError?.response?.data?.detail || submitError?.message || 'Unable to create webhook');
    } finally {
      setIsWebhookSubmitting(false);
    }
  }

  function selectTemplate(templateId) {
    setSelectedTemplateId(templateId);
    if (templateId === 'default') {
      setTemplateName('');
      setTemplateColumns(DEFAULT_EXPORT_TEMPLATE.columns);
      return;
    }
    const matched = savedTemplates.find((item) => item.id === templateId);
    if (!matched) return;
    setTemplateName(matched.name || '');
    setTemplateColumns(Array.isArray(matched.columns) && matched.columns.length ? matched.columns : DEFAULT_EXPORT_TEMPLATE.columns);
  }

  function updateTemplateColumn(index, key, value) {
    setTemplateColumns((previous) => previous.map((item, i) => (i === index ? { ...item, [key]: value } : item)));
  }

  function addTemplateColumn() {
    setTemplateColumns((previous) => [...previous, { label: 'New Column', key: 'work_date' }]);
  }

  function removeTemplateColumn(index) {
    setTemplateColumns((previous) => previous.filter((_, i) => i !== index));
  }

  function saveTemplate() {
    const cleanedName = (templateName || '').trim();
    if (!cleanedName) {
      setError('Template name is required');
      return;
    }
    const cleanedColumns = templateColumns
      .map((column) => ({ label: (column.label || '').trim(), key: column.key }))
      .filter((column) => column.label && column.key);
    if (!cleanedColumns.length) {
      setError('Template must have at least one mapped column');
      return;
    }
    const nextId = selectedTemplateId !== 'default' ? selectedTemplateId : `tpl-${Date.now()}`;
    const nextTemplates = [
      ...savedTemplates.filter((item) => item.id !== nextId),
      { id: nextId, name: cleanedName, columns: cleanedColumns },
    ];
    window.localStorage.setItem(templateStorageKey, JSON.stringify(nextTemplates));
    setSavedTemplates(nextTemplates);
    setSelectedTemplateId(nextId);
    setError('');
  }

  function deleteTemplate() {
    if (selectedTemplateId === 'default') {
      return;
    }
    const nextTemplates = savedTemplates.filter((item) => item.id !== selectedTemplateId);
    window.localStorage.setItem(templateStorageKey, JSON.stringify(nextTemplates));
    setSavedTemplates(nextTemplates);
    setSelectedTemplateId('default');
    setTemplateName('');
    setTemplateColumns(DEFAULT_EXPORT_TEMPLATE.columns);
  }

  function buildCsvHeaders() {
    if (selectedTemplateId === 'default') {
      return DEFAULT_EXPORT_TEMPLATE.columns;
    }
    return templateColumns
      .map((column) => ({ label: (column.label || '').trim(), key: column.key }))
      .filter((column) => column.label && column.key);
  }

  async function exportApprovedTimesheets() {
    setIsExporting(true);
    setError('');
    try {
      let rows = [];
      if ((currentUser?.role || '') === 'manager') {
        const response = await api.get('/api/manager/attendance/timesheets', authHeaders(token, { status_filter: 'approved' }));
        rows = Array.isArray(response.data) ? response.data : [];
      } else {
        const employeeListRes = await api.get('/api/employees', authHeaders(token));
        const employeeList = Array.isArray(employeeListRes.data) ? employeeListRes.data : [];
        const byId = new Map(employeeList.map((employee) => [employee.id, employee]));
        const timesheetBatches = await Promise.all(
          employeeList.map((employee) => api.get(`/api/timesheets/${employee.id}`, authHeaders(token))),
        );
        rows = timesheetBatches
          .flatMap((batch) => (Array.isArray(batch.data) ? batch.data : []))
          .filter((item) => item.status === 'approved')
          .map((item) => {
            const employee = byId.get(item.employee_id);
            return {
              ...item,
              employee_name: employee?.full_name || `Employee ${item.employee_id}`,
              employee_code: employee?.employee_code || `EMP-${item.employee_id}`,
              department: employee?.department || '',
            };
          });
      }

      const monthRows = rows
        .filter((row) => String(row.work_date || '').startsWith(exportMonth))
        .map((row) => ({
          ...row,
          total_hours: Number(row.regular_hours || 0) + Number(row.overtime_hours || 0),
        }));
      const csvHeaders = buildCsvHeaders();
      const csv = toCsv(monthRows, csvHeaders);
      downloadCsv(`timr-approved-timesheets-${exportMonth}.csv`, csv);
    } catch (submitError) {
      setError(submitError?.response?.data?.detail || submitError?.message || 'Unable to export approved timesheets');
    } finally {
      setIsExporting(false);
    }
  }

  const totalHours = selfAttendance.reduce((sum, row) => sum + Number(row.regular_hours || 0) + Number(row.overtime_hours || 0), 0);
  const openSession = selfAttendance.find((row) => row.status === 'pending' && !row.clock_out);
  const apiPullUrl = `${api.defaults.baseURL}/api/v1/timesheets/approved?month=${exportMonth}`;

  return (
    <>
      <section className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2>{currentUser?.full_name || currentUser?.username}</h2>
          <div className="subtle">{currentUser?.role || 'employee'}</div>
        </div>
        <div className="clock-group">
          {canUseSelfAttendance ? (
            <>
              <button onClick={() => void quickClock('clock-in')} disabled={isLoading || Boolean(openSession)}>Clock In</button>
              <button className="lunch" onClick={() => { setOnLunch(true); void quickClock('clock-out'); }} disabled={isLoading || !openSession || onLunch}>Lunch Out</button>
              <button className="lunch" onClick={() => { setOnLunch(false); void quickClock('clock-in'); }} disabled={isLoading || Boolean(openSession) || !onLunch}>Lunch In</button>
              <button className="accent" onClick={() => void quickClock('clock-out')} disabled={isLoading || !openSession}>Clock Out</button>
            </>
          ) : null}
          <button className="secondary" onClick={() => setRefreshKey((v) => v + 1)} disabled={isLoading}>↻</button>
          <button className="secondary" onClick={signOut}>Sign out</button>
        </div>
      </section>

      {!canUseSelfAttendance && activeSection === 'timesheets' ? (
      <section className="card">
        <p className="subtle">This account is in review mode. Personal clock-in and self timesheets are available to employee or manager-linked accounts.</p>
      </section>
      ) : null}

      <section className="card">
        <div className="tabs-row">
          <button className={activeSection === 'timesheets' ? '' : 'secondary'} onClick={() => setActiveSection('timesheets')}>Timesheets</button>
          {canManageEmployees ? (
            <button className={activeSection === 'people' ? '' : 'secondary'} onClick={() => setActiveSection('people')}>People</button>
          ) : null}
          {canExportApproved ? (
            <button className={activeSection === 'payroll-export' ? '' : 'secondary'} onClick={() => setActiveSection('payroll-export')}>Payroll Export</button>
          ) : null}
        </div>
      </section>

      {activeSection === 'timesheets' ? (
      <section className="card">
        <div className="kpi-grid">
          <div className="kpi-card">
            <div className="subtle">Entries</div>
            <div className="kpi">{selfAttendance.length}</div>
          </div>
          <div className="kpi-card">
            <div className="subtle">Logged Hours</div>
            <div className="kpi">{totalHours.toFixed(1)}h</div>
          </div>
          <div className="kpi-card">
            <div className="subtle">Pending Approvals</div>
            <div className="kpi">{pendingTeam.length}</div>
          </div>
        </div>
      </section>
      ) : null}

      {activeSection === 'timesheets' && !canUseSelfAttendance && !canReview ? (
      <section className="card">
        <h3>Timesheets</h3>
        <p className="subtle">Timesheets is open, but this account does not have self-attendance or team-review permissions. Ask an admin to assign employee, manager, or admin review access.</p>
      </section>
      ) : null}

      {error ? <div className="error-banner">{error}</div> : null}

      {activeSection === 'timesheets' && canUseSelfAttendance ? (
      <section className="card">
        <h3>My Recent Timesheets</h3>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>In</th>
                <th>Out</th>
                <th>Hours</th>
                <th>Source</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {selfAttendance.slice(0, 20).map((row) => (
                <tr key={row.id}>
                  <td>{row.work_date}</td>
                  <td>{row.clock_in ? new Date(row.clock_in).toLocaleTimeString() : '—'}</td>
                  <td>{row.clock_out ? new Date(row.clock_out).toLocaleTimeString() : 'Open'}</td>
                  <td>{(Number(row.regular_hours || 0) + Number(row.overtime_hours || 0)).toFixed(2)}</td>
                  <td>{row.attendance_source || 'manual'}</td>
                  <td><span className={`status ${row.status || 'pending'}`}>{row.status || 'pending'}</span></td>
                </tr>
              ))}
              {!selfAttendance.length ? (
                <tr>
                  <td colSpan={6} className="subtle">No timesheets yet.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>
      ) : null}

      {canReview && activeSection === 'timesheets' ? (
        <section className="card">
          <h3>Manager Queue (Pending)</h3>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Date</th>
                  <th>Hours</th>
                  <th>Source</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingTeam.map((row) => (
                  <tr key={row.id}>
                    <td>{row.employee_name || row.employee_code}</td>
                    <td>{row.work_date}</td>
                    <td>{(Number(row.regular_hours || 0) + Number(row.overtime_hours || 0)).toFixed(2)}</td>
                    <td>{row.attendance_source || 'manual'}</td>
                    <td><span className="status pending">pending</span></td>
                    <td className="row" style={{ gap: 8 }}>
                      <button onClick={() => void review(row.id, 'approve')} disabled={isLoading}>Approve</button>
                      <button className="secondary" onClick={() => void review(row.id, 'reject')} disabled={isLoading}>Reject</button>
                    </td>
                  </tr>
                ))}
                {!pendingTeam.length ? (
                  <tr>
                    <td colSpan={6} className="subtle">No pending team timesheets.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {activeSection === 'timesheets' ? (
      <section className="card">
        <h3>QR Attendance Challenge</h3>
        <QrChallengePanel token={token} />
      </section>
      ) : null}

      {canManageEmployees && activeSection === 'people' ? (
        <section className="card">
          <h3>Employee Management</h3>
          <form onSubmit={(event) => void createEmployee(event)} className="form-grid">
            <label>
              Full Name
              <input
                value={employeeForm.full_name}
                onChange={(event) => setEmployeeForm((state) => ({ ...state, full_name: event.target.value }))}
                required
              />
            </label>
            <label>
              Employee Code
              <input
                value={employeeForm.employee_code}
                onChange={(event) => setEmployeeForm((state) => ({ ...state, employee_code: event.target.value }))}
                required
              />
            </label>
            <label>
              Basic Salary
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={employeeForm.basic_salary}
                onChange={(event) => setEmployeeForm((state) => ({ ...state, basic_salary: event.target.value }))}
                required
              />
            </label>
            <label>
              Department
              <input
                value={employeeForm.department}
                onChange={(event) => setEmployeeForm((state) => ({ ...state, department: event.target.value }))}
              />
            </label>
            <label>
              Position
              <input
                value={employeeForm.position}
                onChange={(event) => setEmployeeForm((state) => ({ ...state, position: event.target.value }))}
              />
            </label>
            <label>
              Tax ID
              <input
                value={employeeForm.tax_id}
                onChange={(event) => setEmployeeForm((state) => ({ ...state, tax_id: event.target.value }))}
              />
            </label>
            <div className="form-actions">
              <button type="submit" disabled={isEmployeeSubmitting}>{isEmployeeSubmitting ? 'Saving...' : 'Add Employee'}</button>
            </div>
          </form>

          <div className="import-block">
            <label>
              Bulk Import (.csv or .xlsx)
              <input
                type="file"
                accept=".csv,.xlsx"
                onChange={(event) => setEmployeeImportFile(event.target.files?.[0] || null)}
              />
            </label>
            <button className="secondary" onClick={() => void bulkImportEmployees()} disabled={isEmployeeImporting || !employeeImportFile}>
              {isEmployeeImporting ? 'Importing...' : 'Import Employees'}
            </button>
            <p className="subtle">Template headers supported: employee_code, full_name, basic_salary, department, position.</p>
          </div>
        </section>
      ) : null}

      {canManageEmployees && activeSection === 'people' ? (
        <section className="card">
          <h3>Employees ({employees.length})</h3>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Code</th>
                  <th>Department</th>
                  <th>Position</th>
                  <th>Salary</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((employee) => (
                  <tr key={employee.id}>
                    <td>{employee.full_name}</td>
                    <td>{employee.employee_code}</td>
                    <td>{employee.department || '—'}</td>
                    <td>{employee.position || '—'}</td>
                    <td>{Number(employee.basic_salary || 0).toFixed(2)}</td>
                    <td>
                      <span className={`status ${employee.is_active ? 'approved' : 'rejected'}`}>
                        {employee.is_active ? 'active' : 'inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
                {!employees.length ? (
                  <tr>
                    <td colSpan={6} className="subtle">No employees yet.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {canManageUsers && activeSection === 'people' ? (
        <section className="card">
          <h3>User Provisioning</h3>
          <p className="subtle">Create login accounts for employees, managers, and HR/admin staff within standalone TIMR.</p>
          <form onSubmit={(event) => void createUser(event)} className="form-grid">
            <label>
              Username
              <input
                value={userForm.username}
                onChange={(event) => setUserForm((state) => ({ ...state, username: event.target.value }))}
                required
              />
            </label>
            <label>
              Full Name
              <input
                value={userForm.full_name}
                onChange={(event) => setUserForm((state) => ({ ...state, full_name: event.target.value }))}
                required
              />
            </label>
            <label>
              Role
              <select
                value={userForm.role}
                onChange={(event) => setUserForm((state) => ({ ...state, role: event.target.value, employee_id: event.target.value === 'employee' ? state.employee_id : '' }))}
              >
                <option value="employee">Employee</option>
                <option value="manager">Manager</option>
                <option value="hr">HR</option>
                <option value="finance">Finance</option>
                <option value="admin">Admin</option>
              </select>
            </label>
            {userForm.role === 'employee' ? (
              <label>
                Linked Employee
                <select
                  value={userForm.employee_id}
                  onChange={(event) => setUserForm((state) => ({ ...state, employee_id: event.target.value }))}
                  required
                >
                  <option value="">Select employee</option>
                  {employees.map((employee) => (
                    <option key={employee.id} value={employee.id}>
                      {employee.full_name} ({employee.employee_code})
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <label>
              Temporary Password
              <input
                type="password"
                value={userForm.password}
                onChange={(event) => setUserForm((state) => ({ ...state, password: event.target.value }))}
                required
                minLength={6}
              />
            </label>
            <div className="form-actions">
              <button type="submit" disabled={isUserSubmitting}>{isUserSubmitting ? 'Creating...' : 'Create User Account'}</button>
            </div>
          </form>

          <div className="table-wrap" style={{ marginTop: 16 }}>
            <table>
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Employee Link</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>{user.username}</td>
                    <td>{user.full_name}</td>
                    <td>{user.role}</td>
                    <td>{user.employee_id || '—'}</td>
                  </tr>
                ))}
                {!users.length ? (
                  <tr>
                    <td colSpan={4} className="subtle">No user accounts yet.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {canExportApproved && activeSection === 'payroll-export' ? (
        <section className="card">
          <h3>Approved Timesheet Payroll Export</h3>
          <p className="subtle">Export approved monthly timesheets to CSV and automate integrations with API key pull/webhook flows.</p>
          <div className="row">
            <label>
              Payroll Month
              <input type="month" value={exportMonth} onChange={(event) => setExportMonth(event.target.value)} />
            </label>
            <div style={{ alignSelf: 'end' }}>
              <button onClick={() => void exportApprovedTimesheets()} disabled={isExporting}>
                {isExporting ? 'Exporting...' : 'Download Approved Timesheets CSV'}
              </button>
            </div>
          </div>

          <div className="import-block" style={{ marginTop: 16 }}>
            <h3>Export Template & Column Mapping</h3>
            <div className="row">
              <label>
                Active Template
                <select value={selectedTemplateId} onChange={(event) => selectTemplate(event.target.value)}>
                  <option value="default">{DEFAULT_EXPORT_TEMPLATE.name}</option>
                  {savedTemplates.map((template) => (
                    <option key={template.id} value={template.id}>{template.name}</option>
                  ))}
                </select>
              </label>
              <label>
                Template Name
                <input value={templateName} onChange={(event) => setTemplateName(event.target.value)} placeholder="e.g. SAP payroll format" />
              </label>
            </div>
            {templateColumns.map((column, index) => (
              <div className="row" key={`tpl-col-${index}`}>
                <label>
                  Column Header
                  <input
                    value={column.label}
                    onChange={(event) => updateTemplateColumn(index, 'label', event.target.value)}
                  />
                </label>
                <label>
                  Source Field
                  <select
                    value={column.key}
                    onChange={(event) => updateTemplateColumn(index, 'key', event.target.value)}
                  >
                    {EXPORT_TEMPLATE_SOURCES.map((source) => (
                      <option key={source.key} value={source.key}>{source.label}</option>
                    ))}
                  </select>
                </label>
                <div style={{ alignSelf: 'end' }}>
                  <button type="button" className="secondary" onClick={() => removeTemplateColumn(index)} disabled={templateColumns.length <= 1}>Remove</button>
                </div>
              </div>
            ))}
            <div className="row">
              <button type="button" className="secondary" onClick={addTemplateColumn}>Add Column Mapping</button>
              <button type="button" onClick={saveTemplate}>Save Template</button>
              <button type="button" className="secondary" onClick={deleteTemplate} disabled={selectedTemplateId === 'default'}>Delete Template</button>
            </div>
          </div>

          <div className="import-block" style={{ marginTop: 16 }}>
            <h3>Automated Integration</h3>
            <p className="subtle">Pull endpoint (API key required):</p>
            <p className="subtle helper-code">GET {apiPullUrl}</p>
            <p className="subtle">Webhook event emitted: timr.approved_timesheets.exported</p>
            <p className="subtle">Create API keys below, then configure Integration Webhooks in backend to receive event payloads.</p>
          </div>
        </section>
      ) : null}

      {canManageApiKeys && activeSection === 'payroll-export' ? (
        <section className="card">
          <h3>Integration API Keys</h3>
          <form onSubmit={(event) => void createApiKey(event)} className="form-grid">
            <label>
              Key Name
              <input value={apiKeyForm.name} onChange={(event) => setApiKeyForm({ name: event.target.value })} required />
            </label>
            <div className="form-actions">
              <button type="submit" disabled={isApiKeySubmitting}>{isApiKeySubmitting ? 'Issuing...' : 'Create API Key'}</button>
            </div>
          </form>
          {generatedApiKey ? (
            <div className="import-block" style={{ marginTop: 12 }}>
              <p className="subtle">Copy and store this key now. It is shown only once.</p>
              <p className="subtle helper-code">{generatedApiKey}</p>
            </div>
          ) : null}
          <div className="table-wrap" style={{ marginTop: 16 }}>
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Last Used</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {apiKeys.map((apiKey) => (
                  <tr key={apiKey.id}>
                    <td>{apiKey.name}</td>
                    <td>{apiKey.is_active ? 'active' : 'inactive'}</td>
                    <td>{apiKey.last_used || '—'}</td>
                    <td>{apiKey.created_at || '—'}</td>
                  </tr>
                ))}
                {!apiKeys.length ? (
                  <tr>
                    <td colSpan={4} className="subtle">No API keys yet.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {canManageWebhooks && activeSection === 'payroll-export' ? (
        <section className="card">
          <h3>Webhook Setup</h3>
          <p className="subtle">Register third-party endpoints to receive TIMR integration events with optional request signing.</p>
          <form onSubmit={(event) => void createWebhook(event)} className="form-grid">
            <label>
              Webhook Name
              <input
                value={webhookForm.name}
                onChange={(event) => setWebhookForm((state) => ({ ...state, name: event.target.value }))}
                required
              />
            </label>
            <label>
              Target URL
              <input
                type="url"
                value={webhookForm.target_url}
                onChange={(event) => setWebhookForm((state) => ({ ...state, target_url: event.target.value }))}
                placeholder="https://partner.example.com/hooks/timr"
                required
              />
            </label>
            <label>
              Event Filter
              <input
                value={webhookForm.event_filter}
                onChange={(event) => setWebhookForm((state) => ({ ...state, event_filter: event.target.value }))}
                placeholder="timr.*"
              />
            </label>
            <label>
              Signing Secret (optional)
              <input
                value={webhookForm.signing_secret}
                onChange={(event) => setWebhookForm((state) => ({ ...state, signing_secret: event.target.value }))}
                placeholder="shared-secret"
              />
            </label>
            <label>
              <input
                type="checkbox"
                checked={Boolean(webhookForm.is_active)}
                onChange={(event) => setWebhookForm((state) => ({ ...state, is_active: event.target.checked }))}
              />
              {' '}
              Active
            </label>
            <div className="form-actions">
              <button type="submit" disabled={isWebhookSubmitting}>{isWebhookSubmitting ? 'Saving...' : 'Create Webhook'}</button>
            </div>
          </form>

          <div className="table-wrap" style={{ marginTop: 16 }}>
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Target URL</th>
                  <th>Event Filter</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {webhooks.map((webhook) => (
                  <tr key={webhook.id}>
                    <td>{webhook.name}</td>
                    <td>{webhook.target_url}</td>
                    <td>{webhook.event_filter || '*'}</td>
                    <td>{webhook.is_active ? 'active' : 'inactive'}</td>
                  </tr>
                ))}
                {!webhooks.length ? (
                  <tr>
                    <td colSpan={4} className="subtle">No webhooks configured yet.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
          <p className="subtle" style={{ marginTop: 12 }}>Delivered requests include X-AfriHR-Event and optional X-AfriHR-Signature when signing secret is configured.</p>
        </section>
      ) : null}

      {canReview && activeSection === 'timesheets' ? (
        <>
          <section className="card">
            <h3>Suspicious Entries</h3>
            <SuspiciousPanel token={token} />
          </section>

          <section className="card">
            <h3>Attendance Policy</h3>
            <AttendancePolicyPanel token={token} />
          </section>

          <section className="card">
            <h3>Geofence Sites</h3>
            <GeofenceSitesPanel token={token} />
          </section>

          <section className="card">
            <h3>Kiosk Devices</h3>
            <KioskPanel token={token} />
          </section>
        </>
      ) : null}
    </>
  );
}

function TimrApp() {
  const { token } = useAuth();
  return (
    <main className="timr-shell">
      <div className="timr-container">
        <section className="card">
          <TimrLogo size={62} />
          <p className="subtle" style={{ marginTop: 8 }}>
            Your work hours, tracked and managed — simply.
          </p>
        </section>

        {token ? <TimesheetWorkspace /> : <SignInCard />}
      </div>
    </main>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <TimrApp />
    </AuthProvider>
  );
}
